import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EntryDetails } from '@/api/entries/types/types';
import { EntryItemType } from '@/feature/entries-pickem/components/EntriesList';
import { Currency, DataField, EntryPromotion, GameMode, PromotionType } from '@/types/api.generated';

export type ComputedEntryPromotion =
    | EntryPromotion
    | {
          id: 'betrBucks' | 'mystery' | 'raceToPlace';
          name: string;
          type?: PromotionType;
          metadata?: Array<DataField>;
      };

/**
 * Returns all promotions associated with the given entry.
 * This includes:
 * - Any promotions provided by the BE (`entry.promotions`).
 * - A `Betr Bucks` promotion if the entry is using FRE currency (`Currency.Fre`).
 * - A `Mystery Multiplier` promotion if the entry is in `GameMode.Mystery`.
 *
 * These computed promotions are appended for display purposes,
 * so that all relevant promo labels are shown in the UI, regardless of source.
 *
 * @param entry - The entry object (can be EntryItemType or EntryDetails).
 * @returns A list of `ComputedEntryPromotion` objects to display as promo labels.
 */
export const useComputeEntryPromos = (entry: EntryItemType | EntryDetails): ComputedEntryPromotion[] => {
    const { t } = useTranslation('common');

    return useMemo(() => {
        const promotions: ComputedEntryPromotion[] = [...(entry?.promotions ?? [])];

        if (entry?.currency === Currency.Fre) {
            promotions.push({
                id: 'betrBucks',
                name: t('betr_bucks'),
            });
        }

        if (entry?.gameMode === GameMode.Mystery) {
            promotions.push({
                id: 'mystery',
                name: 'Mystery Multiplier',
            });
        }

        if (entry?.gameMode === GameMode.RaceToPlace) {
            promotions.push({
                id: 'raceToPlace',
                name: 'Race to Place',
            });
        }

        return promotions;
    }, [entry?.promotions, entry?.currency, entry?.gameMode, t]);
};
